import React, { useMemo, useRef, useState, useEffect } from "react";
import "./styles.scss";
import { Dialogo } from "@digix-ui/componentes";
import { Controller, useForm } from "react-hook-form";

import Container from "../../componentes/Container";
import { CampoDeTextoComMascara } from "../../componentes";
import { gerarDiasDaSemana } from "./utils/gerarDiasDaSemana";
import { AgendaItem } from "./utils/agendaItem.type";
import { horas } from "./utils/horas";
import { gerarDiasDoMes } from "./utils/gerarDiasDoMes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buscarAgenda } from "./services/buscarAgenda";
import { mapearParaAgendaItem } from "./utils/mapAgenda";
import {
  criarAgendamento,
  CriarAgendamentoPayload,
} from "./services/criarAgendamento";
import { iniciarAtendimento } from "./services/iniciarAtendimento";
import {
  editarAgendamento,
  EditarAgendamentoPayload,
} from "./services/editarAgendamento";
import { ConverterParaISO } from "./utils/toLocalISO";
import { ObterSegundaDaSemanaAtual } from "./utils/getSemanaAtual";
import { mascaraTelefone } from "./utils/mascaraTel";
import { desmascararCpf } from "./utils/macaraCPF";
import {
  cancelarAgendamento,
  CancelarAgendamentoPayload,
} from "./services/cancelarAgendamento";

import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import ReactDOM from "react-dom";
import { isFuturo } from "./utils/ehFuturo";

const ROTA_SELECIONAR_LOCAL = "/locais-de-atendimento";
type FormNovoAtendimento = {
  nome: string;
  cpf: string;
  telefone: string;
  data: string;
  horaInicio: string;
  horaFim: string;
};

const minutesFromHHmm = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
const diffStepMinutes = (a: string, b: string) =>
  Math.abs(minutesFromHHmm(b) - minutesFromHHmm(a));
const getStepMinutes = (slots: string[]) =>
  slots.length >= 2 ? diffStepMinutes(slots[0], slots[1]) : 30;

type LaneEvent = AgendaItem & {
  startMin: number;
  endMin: number;
  lane?: number;
  statusKey?: string;
  statusLabel: string;
};
type Cluster = {
  id: string;
  startMin: number;
  endMin: number;
  events: LaneEvent[];
  laneCount: number;
};

function buildClusters(list: LaneEvent[]): Cluster[] {
  const sorted = [...list].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin
  );
  const clusters: Cluster[] = [];
  let cur: Cluster | null = null;

  for (const ev of sorted) {
    if (!cur || ev.startMin >= cur.endMin) {
      cur = {
        id: `c-${ev.data.getTime()}-${ev.startMin}`,
        startMin: ev.startMin,
        endMin: ev.endMin,
        events: [ev],
        laneCount: 1,
      };
      clusters.push(cur);
    } else {
      cur.endMin = Math.max(cur.endMin, ev.endMin);
      cur.events.push(ev);
      cur.laneCount = 1;
    }
  }
  return clusters;
}

const MIN_CARD_PX = 44;

const AgendaSemanal: React.FC = () => {
  const dispatch = useDispatch();

  const temLocalSelecionado = useMemo(() => {
    try {
      const raw = localStorage.getItem("localDeAtendimento");
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!obj && typeof obj === "object" && obj?.id != null;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!temLocalSelecionado) {
      dispatch(push(ROTA_SELECIONAR_LOCAL));
    }
  }, [temLocalSelecionado, dispatch]);

  const [modoVisualizacao, setModoVisualizacao] = useState<
    "semanal" | "agenda" | "mensal"
  >("semanal");
  const [menuAbertoId, setMenuAbertoId] = useState<string | null>(null);
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);
  const [mostrarModalCancelar, setMostrarModalCancelar] = useState(false);
  const [justificativaCancelamento, setJustificativaCancelamento] =
    useState("");
  const justificativaRef = useRef<HTMLTextAreaElement | null>(null);
  const [menuPopoverId, setMenuPopoverId] = useState<string | null>(null);

  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );

  React.useEffect(() => {
    if (!openClusterId) setMenuPopoverId(null);
  }, [openClusterId]);

  const [modalEditar, setModalEditar] = React.useState(false);
  const [eventoSelecionado, setEventoSelecionado] =
    React.useState<AgendaItem | null>(null);
  const [modalNovoAtendimento, setModalNovoAtendimento] = React.useState(false);

  const fecharMenu = () => {
    setMenuAbertoId(null);
    setMenuPos(null); // <- limpar posição do portal
  };

  const [inicioSemana, setInicioSemana] = React.useState(
    ObterSegundaDaSemanaAtual
  );

  const SLOT_HEIGHT = React.useMemo(() => {
    if (typeof window === "undefined") return 100;
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue("--slot-height")
      .trim();
    const num = parseFloat(raw);
    return Number.isFinite(num) ? num : 100;
  }, []);

  const ano = inicioSemana.getFullYear();
  const mesIndex0 = inicioSemana.getMonth();

  const { data: apiAgendamentos = [], isLoading } = useQuery({
    queryKey: ["agendamentos", ano, mesIndex0],
    queryFn: () => buscarAgenda(ano, mesIndex0),
    staleTime: 0,
    placeholderData: (prev) => prev,
    enabled: temLocalSelecionado,
  });

  const queryClient = useQueryClient();

  const { mutateAsync: iniciar, isPending: iniciando } = useMutation({
    mutationFn: (id: number) => iniciarAtendimento(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["agendamentos", ano, mesIndex0],
      });
      setMenuAbertoId(null);
    },
  });

  const { mutateAsync: salvarEdicao, isPending: editando } = useMutation({
    mutationFn: (payload: EditarAgendamentoPayload) =>
      editarAgendamento(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["agendamentos", ano, mesIndex0],
      });
      setModalEditar(false);
      setEventoSelecionado(null);
    },
    onError: (error: any) => {
      const resp = error?.response?.data;
      const msgs: string[] =
        resp?.mensagens ??
        (resp?.mensagemDaExcecao ? [resp.mensagemDaExcecao] : []);
      alert(msgs?.join("\n") || "Erro ao editar agendamento.");
    },
  });

  const { mutateAsync: cancelar, isPending: cancelando } = useMutation({
    mutationFn: (payload: CancelarAgendamentoPayload) =>
      cancelarAgendamento(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["agendamentos", ano, mesIndex0],
      });
      setMostrarModalCancelar(false);
      setJustificativaCancelamento("");
      setMenuAbertoId(null);
      setEventoSelecionado(null);
    },
    onError: (error: any) => {
      const resp = error?.response?.data;
      const msg =
        resp?.mensagens?.join("\n") ||
        resp?.mensagemDaExcecao ||
        error?.message ||
        "Erro ao cancelar agendamento.";
      alert(msg);
    },
  });

  const agendasItems = useMemo(
    () => apiAgendamentos.map(mapearParaAgendaItem),
    [apiAgendamentos]
  );

  const diasDaSemana = gerarDiasDaSemana(inicioSemana);

  const {
    control,
    reset,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormNovoAtendimento>();

  const hhmmToMinutes = (v?: string) => {
    if (!v) return 0;
    const [h, m] = v.split(":").map(Number);
    return h * 60 + m;
  };

  const { mutateAsync: salvarAgendamento, isPending: salvando } = useMutation({
    mutationFn: (payload: CriarAgendamentoPayload) => criarAgendamento(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["agendamentos", ano, mesIndex0],
      });
      setModalNovoAtendimento(false);
    },
    onError: (error: any) => {
      const resp = error?.response?.data;
      console.log(resp);
    },
  });

  const handleClickCelula = (dataDoDia: Date, hora: string) => {
    reset({
      nome: "",
      cpf: "",
      telefone: "",
      data: dataDoDia.toLocaleDateString("pt-BR"),
      horaInicio: hora,
      horaFim: hora,
    });
    setModalNovoAtendimento(true);
  };

  const abrirModalEditar = (evento: AgendaItem) => {
    setEventoSelecionado(evento);
    reset({
      nome: evento.nome,
      cpf: evento.cpf,
      telefone: evento.telefone,
      data: evento.data.toLocaleDateString("pt-BR"),
      horaInicio: evento.horaInicio,
      horaFim: evento.horaFim,
    });
    setModalEditar(true);
  };

  const avancarSemana = () => {
    const novaData = new Date(inicioSemana);
    if (modoVisualizacao === "mensal")
      novaData.setMonth(novaData.getMonth() + 1);
    else novaData.setDate(novaData.getDate() + 7);
    setInicioSemana(novaData);
  };

  const voltarSemana = () => {
    const novaData = new Date(inicioSemana);
    if (modoVisualizacao === "mensal")
      novaData.setMonth(novaData.getMonth() - 1);
    else novaData.setDate(novaData.getDate() - 7);
    setInicioSemana(novaData);
  };

  React.useEffect(() => {
    const handleClickFora = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".menu-flutuante") &&
        !target.closest(".botao-opcoes") &&
        !target.closest(".cluster-popover")
      ) {
        setOpenClusterId(null);
        fecharMenu();
      }
    };
    document.addEventListener("click", handleClickFora);
    return () => document.removeEventListener("click", handleClickFora);
  }, []);

  const handleSelecionarMes = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const novoMes = parseInt(event.target.value);
    const novaData = new Date(inicioSemana);
    novaData.setMonth(novoMes);
    setInicioSemana(novaData);
  };

  const onSubmitNovoAtendimento = async (values: FormNovoAtendimento) => {
    const { nome, cpf, telefone, data, horaInicio, horaFim } = values;
    const [dd, mm, yyyy] = (data || "").split("/");
    const base = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    const payload: CriarAgendamentoPayload = {
      dataInicial: ConverterParaISO(base, horaInicio),
      dataFinal: ConverterParaISO(base, horaFim),
      pessoa: {
        nome,
        cpf: desmascararCpf(cpf),
        telefoneCelular: mascaraTelefone(telefone),
      },
    };
    await salvarAgendamento(payload);
  };

  const onSubmitEditar = async (values: FormNovoAtendimento) => {
    if (!eventoSelecionado) return;
    const { nome, cpf, telefone, data, horaInicio, horaFim } = values;
    const [dd, mm, yyyy] = (data || "").split("/");
    const base = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    const payload: EditarAgendamentoPayload = {
      id: (eventoSelecionado as any).id,
      dataInicial: ConverterParaISO(base, horaInicio),
      dataFinal: ConverterParaISO(base, horaFim),
      pessoa: {
        nome,
        cpf: desmascararCpf(cpf),
        telefoneCelular: mascaraTelefone(telefone),
      },
    };
    await salvarEdicao(payload);
  };

  const corpoDoModalNovoAtendimento = () => (
    <div className="modal-novo-atendimento">
      <div className="linha">
        <Controller
          name="nome"
          control={control}
          defaultValue=""
          rules={{ required: "Nome é obrigatório." }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="nome"
                label="Nome"
                mascara=""
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="12"
              />
              {errors.nome && (
                <small className="erro">{errors.nome.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha linha-horizontal">
        <Controller
          name="cpf"
          control={control}
          defaultValue=""
          rules={{
            required: "CPF é obrigatório.",
            validate: (v) =>
              desmascararCpf(v).length === 11 || "CPF deve conter 11 dígitos.",
          }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="cpf"
                label="CPF"
                mascara="999.999.999-99"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="6"
              />
              {errors.cpf && (
                <small className="erro">{errors.cpf.message}</small>
              )}
            </>
          )}
        />

        <Controller
          name="telefone"
          control={control}
          defaultValue=""
          rules={{
            required: "Telefone é obrigatório.",
            validate: (v) =>
              mascaraTelefone(v).length >= 10 ||
              "Informe um telefone válido (DDD + número).",
          }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="telefone"
                label="Telefone"
                mascara="(99) 99999-9999"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="6"
              />
              {errors.telefone && (
                <small className="erro">{errors.telefone.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha">
        <Controller
          name="data"
          control={control}
          defaultValue=""
          rules={{ required: "Data é obrigatória." }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="data"
                label="Data para abertura"
                mascara="99/99/9999"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="12"
              />
              {errors.data && (
                <small className="erro">{errors.data.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha">
        <div className="label-campo">Horário</div>
        <div className="hora-range">
          <div className="campo-hora">
            <Controller
              name="horaInicio"
              control={control}
              defaultValue=""
              rules={{ required: "Horário inicial é obrigatório." }}
              render={({ field }) => (
                <>
                  <input
                    type="time"
                    className="formulario__input"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  {errors.horaInicio && (
                    <small className="erro">{errors.horaInicio.message}</small>
                  )}
                </>
              )}
            />
          </div>

          <span className="ate">até</span>

          <div className="campo-hora">
            <Controller
              name="horaFim"
              control={control}
              defaultValue=""
              rules={{
                required: "Horário final é obrigatório.",
                validate: (value) => {
                  const inicio = getValues("horaInicio");
                  if (!inicio || !value) return true;
                  return (
                    hhmmToMinutes(value) > hhmmToMinutes(inicio) ||
                    "Horário final deve ser maior que o inicial."
                  );
                },
              }}
              render={({ field }) => (
                <>
                  <input
                    type="time"
                    className="formulario__input"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  {errors.horaFim && (
                    <small className="erro">{errors.horaFim.message}</small>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const rodapeDoModalNovoAtendimento = () => (
    <div
      className="rodape-modal"
      style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
    >
      <button
        className="botao botao_contorno botao_cor-secundaria"
        onClick={() => setModalNovoAtendimento(false)}
      >
        Cancelar
      </button>
      <button
        className="botao botao_cor-secundaria botao-confirmar"
        onClick={handleSubmit(onSubmitNovoAtendimento)}
        disabled={salvando}
      >
        {salvando ? "Agendando..." : "Agendar"}
      </button>
    </div>
  );

  const rodapeDoModalEditar = () => (
    <div
      className="rodape-modal"
      style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
    >
      <button
        className="botao botao_contorno botao_cor-secundaria"
        onClick={() => setModalEditar(false)}
        disabled={editando}
      >
        Cancelar
      </button>
      <button
        className="botao botao_cor-secundaria botao-confirmar"
        onClick={handleSubmit(onSubmitEditar)}
        disabled={editando}
      >
        {editando ? "Salvando..." : "Salvar alterações"}
      </button>
    </div>
  );

  const corpoDoModalCancelar = () => (
    <div className="modal-cancelar-atendimento">
      <label htmlFor="justificativa">Justifique o cancelamento</label>
      <textarea
        id="justificativa"
        ref={justificativaRef}
        defaultValue=""
        placeholder="Explique o motivo do cancelamento"
        className="textarea-justificativa"
        style={{
          width: "100%",
          minHeight: "100px",
          marginTop: "0.5rem",
          padding: "0.75rem",
          borderRadius: "8px",
          border: "1px solid #ccc",
          fontSize: "1rem",
          resize: "vertical",
        }}
      />
    </div>
  );

  const rodapeDoModalCancelar = () => (
    <div className="rodape-modal">
      <button
        className="botao botao_contorno botao_cor-secundaria"
        onClick={() => setMostrarModalCancelar(false)}
        disabled={cancelando}
      >
        Voltar
      </button>
      <button
        className="botao botao_cor-secundaria botao-confirmar"
        onClick={async () => {
          if (!eventoSelecionado) return;
          const justificativa = (justificativaRef.current?.value || "").trim();
          if (!justificativa) {
            alert("Informe a justificativa do cancelamento.");
            return;
          }
          await cancelar({
            agendamentoId: (eventoSelecionado as any).id,
            justificativa,
          });
          if (justificativaRef.current) justificativaRef.current.value = "";
        }}
        disabled={cancelando}
      >
        {cancelando ? "Cancelando..." : "Confirmar cancelamento"}
      </button>
    </div>
  );

  if (!temLocalSelecionado) {
    return (
      <Container comFundo tamanhoDoContainer="grande">
        <div className="pagina-agenda">
          <p>Redirecionando para seleção do local de atendimento…</p>
        </div>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container comFundo tamanhoDoContainer="grande">
        <div className="pagina-agenda">
          <p>Carregando agendamentos...</p>
        </div>
      </Container>
    );
  }

  const STEP_MINUTES = getStepMinutes(horas);
  const baseMinutes = minutesFromHHmm(horas[0]);

  return (
    <Container comFundo tamanhoDoContainer="grande">
      <div className="pagina-agenda">
        <div className="agenda-semanal">
          <div className="cabecalho">
            <div className="mes-navegacao">
              <button className="botao-navegacao" onClick={voltarSemana}>
                <i className="fas fa-chevron-left" />
              </button>
              <button className="botao-navegacao" onClick={avancarSemana}>
                <i className="fas fa-chevron-right" />
              </button>
              <div className="select-wrapper">
                <select
                  className="mes-select"
                  value={inicioSemana.getMonth()}
                  onChange={handleSelecionarMes}
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString("pt-BR", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
                <span className="ano">de {inicioSemana.getFullYear()}</span>
              </div>
            </div>

            <div className="exibindo">
              <span className="label">Exibindo:</span>
              <div className="dropdown-wrapper">
                <select
                  value={modoVisualizacao}
                  onChange={(e) =>
                    setModoVisualizacao(
                      e.target.value as "semanal" | "agenda" | "mensal"
                    )
                  }
                >
                  <option value="semanal">Calendário semanal</option>
                  <option value="agenda">Agenda</option>
                  <option value="mensal">Calendário mensal</option>
                </select>
                <i className="fas fa-chevron-down seta-dropdown" />
              </div>
            </div>
          </div>

          {/* ------- AGENDA (lista) ------- */}
          {modoVisualizacao === "agenda" && (
            <div className="agenda-lista">
              {diasDaSemana.map((dia, idx) => {
                const eventosDoDia = agendasItems.filter(
                  (item: any) =>
                    new Date(item.data).toDateString() ===
                    dia.data.toDateString()
                );

                const diaFormatado = dia.data
                  .toLocaleDateString("pt-BR", { weekday: "long" })
                  .replace(/^\w/, (l) => l.toUpperCase());
                const numero = dia.data.getDate().toString().padStart(2, "0");

                return (
                  <div key={idx} className="dia-bloco">
                    <div className="cabecalho-dia-agenda">
                      <span className="numero-dia">{numero}</span>
                      <span>{diaFormatado}</span>
                    </div>

                    {eventosDoDia.length > 0 ? (
                      eventosDoDia.map((item: any) => (
                        <div
                          key={item.id}
                          className={`evento-agenda status-${item.statusKey}`}
                        >
                          <div className="info-horario">
                            <strong>{item.horaInicio}</strong>
                            <small>{`${numero}, ${diaFormatado}`}</small>
                          </div>

                          <div className="info-dados">
                            <p>
                              <strong>Nome</strong> {item.nome}{" "}
                              <span
                                className={`status-pill pill-${item.statusKey}`}
                              >
                                {item.statusLabel}
                              </span>
                            </p>
                            <p>
                              <strong>CPF</strong> {item.cpf}
                            </p>
                            <p>
                              <strong>Telefone</strong> {item.telefone}
                            </p>
                          </div>

                          <div className="acoes">
                            {item.statusKey === "aguardando" && (
                              <>
                                {!isFuturo(item.data, item.horaInicio) && (
                                  <button
                                    className="botao botao_cor-primaria botao_pequeno"
                                    onClick={() => iniciar(item.id)}
                                    disabled={iniciando}
                                  >
                                    <i
                                      className="fas fa-play"
                                      style={{ marginRight: "0.5rem" }}
                                    />
                                    Iniciar atendimento
                                  </button>
                                )}
                                <button
                                  className="botao botao_contorno botao_cor-secundaria botao_pequeno"
                                  onClick={() => abrirModalEditar(item)}
                                >
                                  <i
                                    className="fas fa-pen"
                                    style={{ marginRight: "0.5rem" }}
                                  />
                                  Editar
                                </button>
                                <button
                                  className="botao botao_contorno botao_cor-secundaria botao_pequeno"
                                  onClick={() => {
                                    setEventoSelecionado(item);
                                    setMostrarModalCancelar(true);
                                  }}
                                >
                                  <i
                                    className="fas fa-ban"
                                    style={{ marginRight: "0.5rem" }}
                                  />
                                  Cancelar
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="sem-agendamento">
                        Nenhum atendimento marcado
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {modoVisualizacao === "semanal" && (
            <>
              <div className="dias-header">
                <div className="hora-coluna" />
                {diasDaSemana.map((dia) => (
                  <div key={dia.numero + dia.dia} className="cabecalho-dia">
                    <span className="numero-dia">{dia.dia}</span>
                    <span>{dia.label}</span>
                  </div>
                ))}
              </div>

              <div className="grade">
                <div className="coluna hora-coluna">
                  {horas.map((hora) => (
                    <div key={hora} className="hora">
                      {hora}
                    </div>
                  ))}
                </div>

                {diasDaSemana.map((dia) => {
                  const dayEvents: LaneEvent[] = agendasItems
                    .filter(
                      (ag: any) =>
                        ag.data.getFullYear() === dia.data.getFullYear() &&
                        ag.data.getMonth() === dia.data.getMonth() &&
                        ag.data.getDate() === dia.data.getDate()
                    )
                    .map((ev: any) => ({
                      ...ev,
                      startMin: minutesFromHHmm(ev.horaInicio),
                      endMin: minutesFromHHmm(
                        ev.horaFim || horas[horas.length - 1]
                      ),
                    }));

                  const clusters = buildClusters(dayEvents);

                  return (
                    <div
                      key={dia.numero + dia.dia}
                      className="coluna dia-coluna"
                    >
                      {horas.map((hora) => (
                        <div
                          key={hora}
                          className="celula-hora"
                          onClick={() => handleClickCelula(dia.data, hora)}
                        />
                      ))}

                      <div className="event-layer">
                        {clusters.map((c) => {
                          const primary = [...c.events].sort(
                            (a, b) =>
                              b.endMin - b.startMin - (a.endMin - a.startMin) ||
                              a.startMin - b.startMin
                          )[0];
                          const others = c.events.filter((e) => e !== primary);

                          const topPx =
                            ((primary.startMin - baseMinutes) / STEP_MINUTES) *
                              SLOT_HEIGHT +
                            4;

                          const heightPxRaw =
                            Math.max(
                              1,
                              ((primary.endMin - primary.startMin) /
                                STEP_MINUTES) *
                                SLOT_HEIGHT
                            ) - 8;

                          const heightPx = Math.max(heightPxRaw, MIN_CARD_PX);

                          const clusterTopPx =
                            ((c.startMin - baseMinutes) / STEP_MINUTES) *
                              SLOT_HEIGHT +
                            6;

                          return (
                            <React.Fragment key={c.id}>
                              <div
                                className={`evento-chip status-${primary.statusKey}`}
                                style={{
                                  position: "absolute",
                                  top: Math.round(topPx),
                                  height: Math.round(heightPx),
                                  left: "6px",
                                  width: "calc(100% - 12px)",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEventoSelecionado(primary);
                                  if (primary.statusKey === "aguardando") {
                                    setMenuAbertoId(primary.id.toString());
                                  }
                                }}
                              >
                                <span
                                  className="nome-evento"
                                  title={primary.nome}
                                >
                                  {primary.nome}
                                </span>

                                <span
                                  className={`status-pill pill-${primary.statusKey}`}
                                >
                                  {primary.statusLabel}
                                </span>

                                {primary.statusKey === "aguardando" && (
                                  <button
                                    className="botao-opcoes"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEventoSelecionado(primary);

                                      const novoId = primary.id.toString();
                                      const abrindo = menuAbertoId !== novoId;

                                      const rect = (
                                        e.currentTarget as HTMLElement
                                      ).getBoundingClientRect();
                                      if (abrindo) {
                                        setMenuPos({
                                          top: rect.bottom + window.scrollY,
                                          left:
                                            rect.right + window.scrollX - 140,
                                        });
                                      }

                                      setMenuAbertoId(abrindo ? novoId : null);
                                    }}
                                  >
                                    <i className="fas fa-ellipsis-v" />
                                  </button>
                                )}

                                {menuAbertoId === primary.id.toString() &&
                                  primary.statusKey === "aguardando" &&
                                  menuPos &&
                                  ReactDOM.createPortal(
                                    <div
                                      className="menu-flutuante"
                                      style={{
                                        position: "absolute",
                                        top: menuPos.top,
                                        left: menuPos.left,
                                        zIndex: 10000,
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {!isFuturo(
                                        primary.data,
                                        primary.horaInicio
                                      ) && (
                                        <div
                                          className="opcao-menu"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            await iniciar(primary.id);
                                            fecharMenu();
                                          }}
                                        >
                                          Iniciar
                                        </div>
                                      )}

                                      <div
                                        className="opcao-menu"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          abrirModalEditar(primary);
                                          fecharMenu();
                                        }}
                                      >
                                        Editar
                                      </div>
                                      <div
                                        className="opcao-menu"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEventoSelecionado(primary);
                                          setMostrarModalCancelar(true);
                                          fecharMenu();
                                        }}
                                      >
                                        Cancelar
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                              </div>

                              {others.length > 0 && (
                                <>
                                  <button
                                    className="cluster-more"
                                    style={{
                                      top: clusterTopPx,
                                      right: 8,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenClusterId(
                                        openClusterId === c.id ? null : c.id
                                      );
                                    }}
                                  >
                                    +{others.length} mais
                                  </button>

                                  {openClusterId === c.id && (
                                    <div
                                      className="cluster-popover"
                                      style={{
                                        top: clusterTopPx,
                                        left: 8,
                                        right: 8,
                                      }}
                                    >
                                      <div className="cluster-popover-header">
                                        Atendimentos neste horário
                                      </div>
                                      <div className="cluster-popover-list">
                                        {others.map((ev) => {
                                          const isAguardando =
                                            ev.statusKey === "aguardando";
                                          return (
                                            <div
                                              key={ev.id}
                                              className={`pop-item-row ${
                                                isAguardando ? "" : "disabled"
                                              }`}
                                            >
                                              <div className="left">
                                                <div
                                                  className="title"
                                                  title={ev.nome}
                                                >
                                                  {ev.nome}
                                                </div>
                                                <div className="sub">
                                                  {ev.horaInicio} – {ev.horaFim}
                                                </div>
                                              </div>

                                              {isAguardando && (
                                                <div className="actions">
                                                  {!isFuturo(
                                                    ev.data,
                                                    ev.horaInicio
                                                  ) && (
                                                    <button
                                                      className="btn-xs btn-ok"
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await iniciar(ev.id);
                                                        setOpenClusterId(null);
                                                      }}
                                                      title="Iniciar"
                                                    >
                                                      <i className="fas fa-play" />
                                                      <span>Iniciar</span>
                                                    </button>
                                                  )}

                                                  <button
                                                    className="btn-xs"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setEventoSelecionado(ev);
                                                      setModalEditar(true);
                                                      setOpenClusterId(null);
                                                    }}
                                                    title="Editar"
                                                  >
                                                    <i className="fas fa-pen" />
                                                    <span>Editar</span>
                                                  </button>

                                                  <button
                                                    className="btn-xs btn-danger"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setEventoSelecionado(ev);
                                                      setMostrarModalCancelar(
                                                        true
                                                      );
                                                    }}
                                                    title="Cancelar"
                                                  >
                                                    <i className="fas fa-ban" />
                                                    <span>Cancelar</span>
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {modoVisualizacao === "mensal" && (
            <div className="mensal-grade">
              <div className="linha-semana linha-header">
                {[
                  "Segunda-feira",
                  "Terça-feira",
                  "Quarta-feira",
                  "Quinta-feira",
                  "Sexta-feira",
                  "Sábado",
                  "Domingo",
                ].map((dia, idx) => (
                  <div key={idx} className="cabecalho-dia-mes">
                    {dia}
                  </div>
                ))}
              </div>

              {gerarDiasDoMes(
                inicioSemana.getFullYear(),
                inicioSemana.getMonth()
              ).map((semana, i) => (
                <div key={i} className="linha-semana">
                  {semana.map(({ dia, data }, j) => {
                    const eventosDoDia = agendasItems.filter(
                      (e: any) =>
                        data && e.data.toDateString() === data.toDateString()
                    );
                    return (
                      <div key={j} className="celula-mensal">
                        {dia && (
                          <>
                            <div className="cabecalho-dia-mes">{dia}</div>
                            {eventosDoDia.slice(0, 2).map((evento: any) => (
                              <div
                                key={evento.id}
                                className={`evento-mensal status-${evento.statusKey}`}
                              >
                                {evento.nome}
                              </div>
                            ))}
                            {eventosDoDia.length > 2 && (
                              <div className="evento-mensal">
                                +{eventosDoDia.length - 2}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialogo
          tamanho="mini"
          deveSerExibido={modalNovoAtendimento}
          fecharDialogo={() => setModalNovoAtendimento(false)}
          titulo="Novo atendimento"
          corpo={corpoDoModalNovoAtendimento}
          rodape={rodapeDoModalNovoAtendimento}
        />

        <Dialogo
          tamanho="mini"
          deveSerExibido={modalEditar}
          fecharDialogo={() => setModalEditar(false)}
          titulo="Editar atendimento"
          corpo={corpoDoModalNovoAtendimento}
          rodape={rodapeDoModalEditar}
        />

        <Dialogo
          tamanho="mini"
          deveSerExibido={mostrarModalCancelar}
          fecharDialogo={() => setMostrarModalCancelar(false)}
          titulo="Cancelar atendimento"
          corpo={corpoDoModalCancelar}
          rodape={rodapeDoModalCancelar}
        />
      </div>
    </Container>
  );
};

export default AgendaSemanal;
